import { useState, useCallback } from "react";
import { useGrowthApi } from "../api/use-growth-api";
import { BrandDetails } from "../types/add-brand-types";
import useCreatorStudioApi from "../api/use-creator-studio-api";
import { ContentFocus, ContentMode } from "../types/creator-studio";
import { useNavigation } from "./useNavigation";
import { AppRoute } from "../types/routes";
import { isNotEmpty } from "../utils/type-utils";
import useSeoArticleApi from "../api/use-seo-article.api";
import useWeeklyPlanApi from "../api/use-weekly-plan-api";
import { dispatchWeeklyPlanRefresh } from "../utils/weekly-plan-events";

type GrowthTab = "monitor" | "seo" | "leads";
const PRODUCT_LED_SEO_ANGLE = "product_led";

const escapeRegExp = (value: string) =>
  value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const removeBrandNameFromKeyword = (keyword: string, brandName?: string) => {
  if (!brandName?.trim()) {
    return keyword;
  }
  const brandPattern = new RegExp(escapeRegExp(brandName.trim()), "gi");
  return (
    keyword.replace(brandPattern, "").replace(/\s+/g, " ").trim() || keyword
  );
};

const buildSeoGenerationPrompt = (
  keyword: string,
  brandName: string | undefined,
  contentFocus: ContentFocus
) => {
  if (contentFocus === ContentFocus.ABOUT_PRODUCT) {
    return keyword;
  }

  const brandInstruction = brandName
    ? ` Do not mention ${brandName} or position the article around that product.`
    : "";
  const generalKeyword = removeBrandNameFromKeyword(keyword, brandName);

  return [
    "Write a non-branded educational SEO article for this topic or search intent:",
    `${generalKeyword}.${brandInstruction}`,
    "Keep it useful for the reader even if they have never heard of the product.",
  ].join(" ");
};

const useGrowth = (
  brandDetails: BrandDetails,
  initialTab: GrowthTab = "seo"
) => {
  const [activeTab, setActiveTab] = useState<GrowthTab>(initialTab);
  const [loading, setLoading] = useState(false);
  const { updateMonitoring } = useGrowthApi();
  const { generateCampaign } = useCreatorStudioApi();
  const { getSavedSeoArticle, saveSeoArticle } = useSeoArticleApi();
  const { updateWeeklyPlanItem } = useWeeklyPlanApi();
  const { push } = useNavigation();
  const [toast, setToast] = useState<{
    message: string;
    type: "success" | "error" | "info" | "warning";
    isVisible: boolean;
  } | null>(null);

  const onUpdateKeywordClick = useCallback(
    async (keywords: string[], platforms: string[]) => {
      const response = await updateMonitoring(
        keywords,
        "keywords",
        brandDetails.id,
        platforms
      );

      if (response?.status === "success") {
        setToast({
          message: `Updated Successfully`,
          type: "success",
          isVisible: true,
        });
      }
    },
    [brandDetails.id, updateMonitoring]
  );

  const onUpdateCompetitorsClick = useCallback(
    async (keywords: string[]) => {
      await updateMonitoring(keywords, "competitors", brandDetails.id);
    },
    [brandDetails.id, updateMonitoring]
  );

  const markPlannedSeoDrafted = useCallback(
    async (weeklyPlanItemId?: string) => {
      if (!weeklyPlanItemId) {
        return;
      }
      const updated = await updateWeeklyPlanItem(weeklyPlanItemId, {
        status: "drafted",
      });
      if (updated) {
        dispatchWeeklyPlanRefresh(updated.brand_id);
      }
    },
    [updateWeeklyPlanItem]
  );

  const onSeoItemClick = useCallback(
    async (keyword: string, weeklyPlanItemId?: string, angle?: string) => {
      const normalizedAngle = angle || "educational";
      const contentFocus =
        normalizedAngle === PRODUCT_LED_SEO_ANGLE
          ? ContentFocus.ABOUT_PRODUCT
          : ContentFocus.GENERAL_POST;
      const selectedKnowledge =
        contentFocus === ContentFocus.ABOUT_PRODUCT
          ? Object.keys(brandDetails.knowledge_base || {})
          : [];
      const generationPrompt = buildSeoGenerationPrompt(
        keyword,
        brandDetails.brand_name,
        contentFocus
      );
      setLoading(true);
      const savedArticle = await getSavedSeoArticle(
        brandDetails.id,
        keyword,
        normalizedAngle
      );
      const savedMarkdown = savedArticle?.content_markdown ?? "";
      if (isNotEmpty(savedMarkdown)) {
        await markPlannedSeoDrafted(weeklyPlanItemId);
        push(AppRoute.SEO_PREVIEW.replace(":brandId", brandDetails.id), {
          markdown: savedMarkdown,
          keyword,
          title: savedArticle?.title,
          description: savedArticle?.description,
          tags: savedArticle?.tags,
          isSaved: true,
        });
        setLoading(false);
        return;
      }

      const response = await generateCampaign(
        generationPrompt,
        brandDetails.id,
        selectedKnowledge,
        ContentMode.SEO_ARTICLE,
        contentFocus
      );

      if (isNotEmpty(response) && isNotEmpty(response?.drafts)) {
        const draft = response.drafts[0];
        const markdownContent =
          typeof draft === "string" ? draft : draft?.body ?? "";
        const metadata =
          typeof draft === "string"
            ? { angle: normalizedAngle }
            : {
                title: draft?.title,
                description: draft?.description,
                tags: draft?.tags,
                angle: normalizedAngle,
              };
        await saveSeoArticle(
          brandDetails.id,
          keyword,
          markdownContent,
          metadata,
          weeklyPlanItemId
        );
        if (weeklyPlanItemId) {
          dispatchWeeklyPlanRefresh(brandDetails.id);
        }
        push(AppRoute.SEO_PREVIEW.replace(":brandId", brandDetails.id), {
          markdown: markdownContent,
          keyword,
          title: metadata?.title,
          description: metadata?.description,
          tags: metadata?.tags,
          isSaved: true,
        });
      }

      setLoading(false);
    },
    [
      brandDetails.id,
      brandDetails.brand_name,
      brandDetails.knowledge_base,
      generateCampaign,
      getSavedSeoArticle,
      markPlannedSeoDrafted,
      push,
      saveSeoArticle,
    ]
  );

  const handleTabChange = useCallback((tab: GrowthTab) => {
    setActiveTab(tab);
  }, []);

  return {
    activeTab,
    loading,
    handleTabChange,
    updateMonitoring,
    onUpdateKeywordClick,
    onUpdateCompetitorsClick,
    onSeoItemClick,
    toast,
    setToast,
  };
};

export default useGrowth;
